import { type FocusEvent } from 'react';
import JSZip from 'jszip';
import { isElementInViewport } from './formHelperFunctions';
import { getFilenameFromUrl } from './fileNames';

export function runningInClient() {
  // eslint-disable-next-line no-restricted-globals
  return typeof window === 'object';
}

// Safeguard for NextJS support
export function featheryDoc() {
  // eslint-disable-next-line no-restricted-globals
  return runningInClient() ? document : ({} as any);
}

export function featheryWindow() {
  // eslint-disable-next-line no-restricted-globals
  return runningInClient() ? window : ({} as any);
}

export const isHoverDevice = () =>
  featheryWindow().matchMedia('(hover: hover)').matches;

export const isTouchDevice = () =>
  featheryWindow().matchMedia('(pointer: coarse)').matches;

// Returns whether or not user device is running iOS
// based on: https://stackoverflow.com/a/76302335
export const isIOS = () => {
  let userAgentString = navigator.userAgent;
  const uaData = (navigator as any).userAgentData;
  if (uaData != null && uaData.brands) {
    userAgentString = uaData.brands
      .map((item: any) => item.brand + '/' + item.version)
      .join(' ');
  }
  return /iPad|iPhone|iPod/.test(userAgentString);
};

export const isAndroid = () => {
  return /(android)/i.test(navigator.userAgent);
};

export const isMobile = () => isIOS() || isAndroid();

export const hoverStylesGuard = (styles: any) =>
  isHoverDevice() ? styles : {};

/**
 * @param key string corresponding to cookie name
 * @returns cookie value, or undefined if not found
 */
export function getCookie(key: string) {
  return featheryDoc()
    .cookie.split('; ')
    .filter((row: string) => row.startsWith(`${key}=`))
    .map((c: string) => c.split('=')[1])[0];
}

const ONE_YEAR_SECONDS = 31536000;

/**
 * @param maxAge how long the cookie lives, in seconds. Defaults to a year,
 * which is what every identity cookie the SDK writes has always used.
 */
export function setCookie(key: string, val: string, maxAge = ONE_YEAR_SECONDS) {
  featheryDoc().cookie = `${key}=${val}; max-age=${maxAge}; SameSite=strict; path=/;`;
}

export function deleteCookie(key: string) {
  featheryDoc().cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export function getStytchJwt() {
  return getCookie('stytch_session_jwt');
}

export const openTab = (url: any) =>
  featheryWindow().open(url, '_blank', 'noopener');

export function downloadFile(file: File) {
  const element = featheryDoc().createElement('a');
  element.style.display = 'none';
  const href = featheryWindow().URL.createObjectURL(file);
  element.href = href;
  element.download = file.name;
  featheryDoc().body.appendChild(element);

  element.click();

  featheryWindow().URL.revokeObjectURL(href);
  featheryDoc().body.removeChild(element);
}

async function getFileData(url: string) {
  const response = await fetch(url);
  const blob = await response.blob();
  const fileName = getFilenameFromUrl(url);
  return { fileName, blob };
}

export async function downloadAllFileUrls(urls: string[], zipName?: string) {
  if (urls.length === 0) return;

  let file: File;

  if (urls.length > 1) {
    const zip = new JSZip();

    const files = await Promise.all(
      urls.map((url: string) => getFileData(url))
    );

    const nameCount: Record<string, number> = {};
    for (const { fileName, blob } of files) {
      let uniqueName = fileName;
      if (nameCount[fileName] != null) {
        nameCount[fileName]++;
        const dotIndex = fileName.lastIndexOf('.');
        if (dotIndex !== -1) {
          uniqueName = `${fileName.slice(0, dotIndex)} (${
            nameCount[fileName]
          })${fileName.slice(dotIndex)}`;
        } else {
          uniqueName = `${fileName} (${nameCount[fileName]})`;
        }
      } else {
        nameCount[fileName] = 0;
      }
      zip.file(uniqueName, blob);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const finalZipName =
      zipName && !zipName.endsWith('.zip') ? `${zipName}.zip` : zipName;
    file = new File([zipBlob], finalZipName || 'Feathery_Download.zip', {
      type: 'application/zip'
    });
  } else {
    const { fileName, blob } = await getFileData(urls[0]);
    file = new File([blob], fileName, { type: blob.type });
  }

  downloadFile(file);
}

// iOS devices do not scroll to focused radio buttons
// and checkboxes so we manually scroll to maintain a
// consistent user experience.
//
// scroll to element if it's not in viewport and an iOS device
export function iosScrollOnFocus(event: FocusEvent) {
  if (!isIOS()) return;
  const element = event.target;
  if (
    element &&
    element instanceof HTMLElement &&
    !isElementInViewport(element)
  ) {
    element.scrollIntoView();
  }
}

export function devicePixelRatio() {
  return featheryWindow().devicePixelRatio || 1;
}
